import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import {
  selectRecentMessages,
  selectIsDiscussionLoading,
  selectResponseLoading,
  selectChatError,
  loadMessages,
  sendMessage,
} from '../../element-editor/store/chat';
import { ChatMessage } from '../../element-editor/store/chat/chat.types';
import { FileService, FileMetaData, ProcessedFile } from '../../services/file/file.service';
import { loadHome } from '../../home/home.actions';
import { selectOrganisations } from '../../organisation/organisation.selector';

@Component({
  selector: 'nw-chat-interface',
  templateUrl: './chat-interface.component.html',
  styleUrls: ['./chat-interface.component.scss'],
})
export class ChatInterfaceComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageList') messageList: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  messages$: Observable<ChatMessage[]>;
  isDiscussionLoading$: Observable<boolean>;
  isResponseLoading$: Observable<boolean>;
  error$: Observable<string | null>;

  inputValue = '';
  pendingFiles: ProcessedFile[] = [];
  isDragging = false;
  filePreviewUrlsById: Record<string, string> = {};
  private shouldScroll = false;
  private organisationID: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly fileService: FileService,
  ) {
    this.messages$ = this.store.select(selectRecentMessages);
    this.isDiscussionLoading$ = this.store.select(selectIsDiscussionLoading);
    this.isResponseLoading$ = this.store.select(selectResponseLoading);
    this.error$ = this.store.select(selectChatError);
  }

  ngOnInit() {
    this.store.dispatch(loadHome());

    this.store
      .select(selectOrganisations)
      .pipe(
        filter(orgs => orgs?.length > 0),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(organisations => {
        this.organisationID = organisations[0].id;
        this.store.dispatch(loadMessages({ organisationID: this.organisationID }));
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  async onFilesDropped({ files }: { files: FileList }) {
    for (const file of Array.from(files)) {
      const processed = await this.fileService.processFile(file);
      this.pendingFiles.push(processed);
    }
  }

  triggerFileInput() {
    this.fileInput?.nativeElement?.click();
  }

  async onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    for (const file of Array.from(input.files)) {
      const processed = await this.fileService.processFile(file);
      this.pendingFiles.push(processed);
    }

    input.value = '';
  }

  removePendingFile(index: number, event: Event) {
    event.stopPropagation();
    const removed = this.pendingFiles.splice(index, 1)[0];
    if (removed?.preview?.objectUrl) {
      URL.revokeObjectURL(removed.preview.objectUrl);
      delete this.filePreviewUrlsById[removed.meta.id];
    }
  }

  getPreviewUrl(file: FileMetaData): string | undefined {
    return this.filePreviewUrlsById[file.id] || file.url;
  }

  async send() {
    const content = this.inputValue.trim();
    if (!content && !this.pendingFiles.length) return;
    if (!this.organisationID) return;

    const processedFiles = [...this.pendingFiles];
    this.pendingFiles = [];
    this.inputValue = '';
    this.shouldScroll = true;

    // Store preview URLs before clearing pending files
    for (const f of processedFiles) {
      if (f.preview?.objectUrl) {
        this.filePreviewUrlsById[f.meta.id] = f.preview.objectUrl;
      }
    }

    // Upload files in background
    if (processedFiles.length) {
      this.fileService.uploadFles(processedFiles).catch(err =>
        console.error('File upload failed', err),
      );
    }

    const files: FileMetaData[] = processedFiles.map(f => f.meta);

    this.store.dispatch(sendMessage({
      organisationID: this.organisationID,
      content: content || '',
      files: files.length ? files : undefined,
    }));
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  private scrollToBottom() {
    const el = this.messageList?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }
}
